/**
 * CART GIFT & CROSS-SELL
 * ----------------------------------------------------------------------------------------------------------------------
 * Native replacement for MonsterUpsell's cart drawer behaviour:
 *  - window.openNativeCartDrawer(): refreshes + opens the theme's own <cart-drawer>. Drop-in replacement for the
 *    `monster_refresh()` calls that used to live in bundle-builder.liquid, toggle-cross-sell.js, footer-group.json
 *    and the retired Treedify compatibility snippet.
 *  - <cart-progress-bar>: two-tier progress bar (free shipping + free gift), mirrors free-shipping-bar.js.
 *  - <cart-gift-picker>: color picker dialog that adds the chosen 0€ gift variant to the cart.
 *  - Cross-sell quick add buttons + iPhone model auto-match (reuses the site-wide `iphoneType` localStorage key
 *    already set by assets/custom.js and assets/toggle-cross-sell.js on `variant:change`).
 */

(function () {
  "use strict";

  var CART_DRAWER_SECTION_ID = "cart-drawer";

  /**
   * Refreshes the cart-drawer section from the server and opens it, exactly like the native add-to-cart flow does.
   * Safe to call from anywhere on the site (product page cross-sell toggles, bundle builder, third-party app
   * add-to-cart callbacks, ...).
   */
  window.openNativeCartDrawer = function openNativeCartDrawer(items) {
    return fetch(Shopify.routes.root + "?section_id=" + CART_DRAWER_SECTION_ID)
      .then(function (response) {
        return response.text();
      })
      .then(async function (html) {
        var cart = items ? await fetch(Shopify.routes.root + "cart.js").then(function (response) { return response.json(); }).catch(function () { return {}; }) : {};
        var sections = {};
        sections[CART_DRAWER_SECTION_ID] = html;

        document.dispatchEvent(
          new CustomEvent("cart:change", {
            detail: {
              cart: Object.assign(cart, { sections: sections }),
              items: items,
              baseEvent: "variant:add"
            }
          })
        );
      })
      .catch(function (error) {
        console.error("openNativeCartDrawer failed:", error);
      });
  };

  /* ============================================================================================================
     PROGRESS BAR (free shipping + free gift)
     ============================================================================================================ */

  function parseAmount(value) {
    return parseFloat(String(value || "0").replace(/[^0-9.]/g, "")) || 0;
  }

  function isGiftLine(item) {
    return !!(item.properties && item.properties._gift_item === "true");
  }

  function hasDiscountedCordon(cart, productIds) {
    return cart.items.some(function (item) {
      if (productIds.indexOf(String(item.product_id)) === -1 || item.original_price <= 0) return false;
      return (item.line_level_discount_allocations || []).some(function (allocation) {
        return allocation.amount >= item.original_price;
      });
    });
  }

  // cart.item.line_price already reflects order-level discount allocations on this store (unlike final_line_price,
  // which stays at the pre-discount amount for that kind of discount) — see the matching note in
  // snippets/cart-progress-bar.liquid. Summing it and excluding the gift line's own is correct on its own; do not
  // also subtract cart.cart_level_discount_applications on top, that would double-count it.
  function cartTotalExcludingGift(cart) {
    return cart.items
      .filter(function (item) {
        return !isGiftLine(item);
      })
      .reduce(function (sum, item) {
        return sum + item.line_price;
      }, 0);
  }

  // Where the two milestones sit on the bar, as a percentage of its width. Fixed positions, deliberately not the
  // thresholds' real position on a money scale — see fillPercent() below.
  var SHIPPING_TICK_PERCENT = 50;
  var GIFT_TICK_PERCENT = 90;
  var MIN_LABEL_GAP = 12; // px of breathing room between the two labels, at minimum

  // Centres a label on `percent` of the bar, clamped so it never hangs off either end. Returns its left offset.
  function placeLabel(label, trackWidth, percent) {
    var left = (trackWidth * percent) / 100 - label.offsetWidth / 2;
    left = Math.max(0, Math.min(left, trackWidth - label.offsetWidth));

    label.style.left = left + "px";
    label.style.right = "auto";
    label.style.transform = "none";

    return left;
  }

  window.customElements.get("cart-progress-bar") ||
    window.customElements.define(
      "cart-progress-bar",
      class extends HTMLElement {
        static get observedAttributes() {
          return ["total-price", "has-gift"];
        }

        connectedCallback() {
          this._shippingThreshold = parseAmount(this.getAttribute("shipping-threshold")) * 100;
          this._giftThreshold = parseAmount(this.getAttribute("gift-threshold")) * 100;
          this._giftProductIds = (this.getAttribute("gift-product-ids") || "").split(",");
          this._international = this.hasAttribute("international");

          // The dialog relocates its content to <body> once it opens, which disconnects/reconnects this element —
          // guard against attaching the document-level listener twice.
          if (!this._onCartChange) {
            this._onCartChange = this.onCartChange.bind(this);
          }
          document.removeEventListener("cart:change", this._onCartChange);
          document.addEventListener("cart:change", this._onCartChange);
          this.update();

          // Labels can only be positioned once they have a width, and they have none while the drawer is closed
          // (nothing is laid out) — so re-run as soon as the bar actually gets a size, and on every later resize.
          var labels = this.querySelector(".cart-progress-bar__labels");
          if (labels && window.ResizeObserver && !this._resizeObserver) {
            this._resizeObserver = new ResizeObserver(this.positionLabels.bind(this));
            this._resizeObserver.observe(labels);
          }
        }

        disconnectedCallback() {
          document.removeEventListener("cart:change", this._onCartChange);

          if (this._resizeObserver) {
            this._resizeObserver.disconnect();
            this._resizeObserver = null;
          }
        }

        onCartChange(event) {
          var cart = event.detail && event.detail.cart;
          if (!cart || !cart.items) return;

          var hasSelectedGift = cart.items.some(isGiftLine);
          var total = cartTotalExcludingGift(cart);
          var hasGift = hasSelectedGift ||
            (total >= this._giftThreshold && hasDiscountedCordon(cart, this._giftProductIds));

          this.setAttribute("total-price", total);
          this.setAttribute("has-gift", hasGift ? "true" : "false");

          // Auto-remove the gift if the cart dropped back under the threshold.
          if (hasSelectedGift && total < this._giftThreshold) {
            this.removeGift(cart);
          }
        }

        removeGift(cart) {
          var giftLine = cart.items.find(isGiftLine);
          if (!giftLine) return;

          fetch(Shopify.routes.root + "cart/change.js", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ id: giftLine.key, quantity: 0 })
          })
            .then(function () {
              window.openNativeCartDrawer();
            })
            .catch(function (error) {
              console.error("Failed to remove expired gift:", error);
            });
        }

        attributeChangedCallback() {
          if (this._giftThreshold === undefined) return; // not connected yet
          this.update();
        }

        update() {
          var total = parseAmount(this.getAttribute("total-price"));
          var hasGift = this.getAttribute("has-gift") === "true";

          // A single-goal bar: international visitors (no free-shipping promise to show), or a misconfigured pair
          // of thresholds where the shipping one isn't actually the first milestone.
          this._singleGoal =
            this._international || this._shippingThreshold <= 0 || this._shippingThreshold >= this._giftThreshold;

          this.style.setProperty("--cart-progress-fill", this.fillPercent(total) + "%");
          this.style.setProperty("--cart-progress-tick-shipping", SHIPPING_TICK_PERCENT + "%");
          this.style.setProperty("--cart-progress-tick-gift", GIFT_TICK_PERCENT + "%");

          var shippingReached = this._singleGoal || total >= this._shippingThreshold;
          this.classList.toggle("cart-progress-bar--shipping-reached", shippingReached);
          this.classList.toggle("cart-progress-bar--gift-reached", total >= this._giftThreshold);

          this.positionLabels();

          var messageEl = this.querySelector("[data-cart-progress-message]");
          if (messageEl) {
            var message;
            if (!this._international && total < this._shippingThreshold) {
              message = this.getAttribute("remaining-shipping-message").replace(
                "{{ remaining_amount }}",
                formatMoneyFallback(this._shippingThreshold - total)
              );
            } else if (total < this._giftThreshold) {
              message = this.getAttribute("remaining-gift-message").replace(
                "{{ remaining_amount }}",
                formatMoneyFallback(this._giftThreshold - total)
              );
            } else if (!hasGift) {
              message = this.getAttribute("gift-unlocked-message");
            } else {
              message = this.getAttribute("all-unlocked-message");
            }
            messageEl.innerHTML = message;
          }

          this.updateGiftBox(total, hasGift);
        }

        // The bar is piecewise, not linear in money: the cart fills the first half of it on the way to free
        // shipping, then the next 40% on the way to the gift, then the last 10% once both are unlocked. On a linear
        // scale 50€ and 65€ sit at 77% and 100%, which is what jammed the two markers (and their labels) together.
        fillPercent(total) {
          var gift = Math.max(this._giftThreshold, 1);

          if (this._singleGoal) {
            return total >= gift ? 100 : (total / gift) * GIFT_TICK_PERCENT;
          }

          var shipping = this._shippingThreshold;
          if (total >= gift) return 100;
          if (total >= shipping) {
            return (
              SHIPPING_TICK_PERCENT +
              ((total - shipping) / (gift - shipping)) * (GIFT_TICK_PERCENT - SHIPPING_TICK_PERCENT)
            );
          }
          return (total / shipping) * SHIPPING_TICK_PERCENT;
        }

        // Each label is centred under its own marker, kept inside the bar, and — when the two would still end up
        // touching (a long translation, a very narrow drawer) — the shipping one is pushed left until there's a
        // real gap between them. Percentage-only CSS can't do that last part, and the labels running into each
        // other is exactly what this bar kept doing.
        positionLabels() {
          var labels = this.querySelector(".cart-progress-bar__labels");
          if (!labels) return;

          var trackWidth = labels.offsetWidth;
          if (!trackWidth) return; // drawer still closed: nothing is laid out yet, the ResizeObserver will call back

          var giftLabel = this.querySelector("[data-label-gift]");
          var giftLeft = null;

          if (giftLabel) {
            giftLeft = placeLabel(giftLabel, trackWidth, GIFT_TICK_PERCENT);
          }

          var shippingLabel = this.querySelector("[data-label-shipping]");
          if (!shippingLabel || this._singleGoal) return;

          var shippingLeft = placeLabel(shippingLabel, trackWidth, SHIPPING_TICK_PERCENT);
          if (giftLeft === null) return;

          var overlap = shippingLeft + shippingLabel.offsetWidth + MIN_LABEL_GAP - giftLeft;
          if (overlap > 0) {
            shippingLabel.style.left = Math.max(0, shippingLeft - overlap) + "px";
          }
        }

        // The gift box (snippets/cart-gift-picker.liquid) is a separate, non-sticky sibling of
        // .cart-drawer__sticky-top (which wraps this element) — not a direct sibling of <cart-progress-bar>
        // itself, so this needs to search from a common ancestor rather than this.parentElement.
        updateGiftBox(total, hasGift) {
          var root = this.closest("cart-drawer") || this.parentElement;
          var box = root && root.querySelector("[data-gift-box]");
          if (!box) return;

          box.hidden = hasGift;
          if (hasGift) return;

          var unlocked = total >= this._giftThreshold;

          var teaser = box.querySelector("[data-gift-teaser]");
          if (teaser) {
            teaser.hidden = unlocked;
            if (!unlocked) {
              var template = teaser.getAttribute("data-template");
              if (template) {
                teaser.innerHTML = template.replace("{{ remaining_amount }}", formatMoneyFallback(this._giftThreshold - total));
              }
            }
          }

          var unlockedBlock = box.querySelector("[data-gift-unlocked]");
          if (unlockedBlock) unlockedBlock.hidden = !unlocked;

          var ctaButton = box.querySelector("[data-gift-cta-button]");
          if (ctaButton) ctaButton.hidden = !unlocked;
        }
      }
    );

  function formatMoneyFallback(cents) {
    var vars = window.themeVariables && window.themeVariables.settings;
    var amount = (cents / 100).toFixed(2).replace(".", ",");
    if (!vars) return amount + " €";
    var format = vars.currencyCodeEnabled ? vars.moneyWithCurrencyFormat : vars.moneyFormat;
    return format ? format.replace("{{amount_with_comma_separator}}", amount).replace("{{amount}}", (cents / 100).toFixed(2)) : amount + " €";
  }

  // Shared by cart-cross-sell's auto-match and manual dropdown handlers: swaps a card's price, compare-at price
  // and photo to match the given variant option (see the JSON built in snippets/cart-cross-sell-card.liquid).
  function applyCardVariant(card, option) {
    var priceLabel = card.querySelector("[data-model-label]");
    if (priceLabel) priceLabel.textContent = formatMoneyFallback(option.price);

    var comparePrice = card.querySelector("[data-compare-price]");
    if (comparePrice) {
      var onSale = option.compare_at_price > option.price;
      comparePrice.hidden = !onSale;
      if (onSale) comparePrice.textContent = formatMoneyFallback(option.compare_at_price);
    }

    var image = card.querySelector("[data-card-image]");
    if (image && option.image) {
      image.src = option.image;
      image.removeAttribute("srcset");
    }
  }

  /* ============================================================================================================
     GIFT PICKER
     ============================================================================================================ */

  async function giftCartRequest(path, body) {
    var response = await fetch(Shopify.routes.root + path, body === undefined ? undefined : {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body)
    });
    var result = await response.json();
    if (!response.ok) throw new Error(result.description || result.message || "Cart update failed");
    return result;
  }

  function matchingCartLine(cart, target) {
    var properties = target.properties || {};
    return cart.items.find(function (item) {
      if (Number(item.id) !== Number(target.id)) return false;
      var itemProperties = item.properties || {};
      var keys = Object.keys(properties);
      return keys.length === Object.keys(itemProperties).length && keys.every(function (key) {
        return itemProperties[key] === properties[key];
      });
    });
  }

  document.addEventListener("click", function (event) {
    var trigger = event.target.closest("button[aria-controls]");
    if (!trigger) return;
    var modal = document.getElementById(trigger.getAttribute("aria-controls"));
    var picker = modal && modal.querySelector("cart-gift-picker");
    if (picker) picker._changeLineKey = trigger.getAttribute("data-gift-change-key");
  }, true);

  window.customElements.get("cart-gift-picker") ||
    window.customElements.define(
      "cart-gift-picker",
      class extends HTMLElement {
        connectedCallback() {
          // The dialog relocates its content to <body> once it opens, which disconnects/reconnects this element —
          // without this guard, a second connectedCallback would attach a second set of listeners, causing every
          // click to add the gift twice.
          if (this._listenersAttached) return;
          this._listenersAttached = true;

          this.addEventListener("click", this.onClick.bind(this));
          this.addEventListener("keydown", this.onKeydown.bind(this));
        }

        onKeydown(event) {
          if (event.key !== "Enter" && event.key !== " ") return;
          var swatch = event.target.closest("[data-gift-variant-id]");
          if (!swatch) return;
          event.preventDefault();
          this.onClick(event);
        }

        onClick(event) {
          var zoomTrigger = event.target.closest("[data-gift-zoom]");
          if (zoomTrigger) {
            // Don't treat this as a color selection, but let the click keep bubbling to document.body: that's
            // where DialogElement's delegated listener lives (it opens any element referenced by this button's
            // aria-controls), so stopping propagation here would silently break the zoom view entirely.
            return;
          }

          var swatch = event.target.closest("[data-gift-variant-id]");
          if (!swatch || swatch.getAttribute("aria-disabled") === "true") return;

          this.selectGift(swatch);
        }

        async selectGift(swatch) {
          var variantId = Number(swatch.getAttribute("data-gift-variant-id"));
          if (!variantId || this.classList.contains("is-loading")) return;

          this.classList.add("is-loading");
          var modal = this.closest("x-drawer, x-modal");

          var addedTarget = null;
          try {
            var cart = await giftCartRequest("cart.js");
            var target = this._changeLineKey && cart.items.find(function (item) {
              return item.key === this._changeLineKey;
            }.bind(this));
            if (this._changeLineKey && !target) throw new Error("Gift line no longer exists");

            var replacingRegularLine = target && !isGiftLine(target);
            if (replacingRegularLine) {
              var progress = document.querySelector("cart-progress-bar");
              var productIds = (progress && progress.getAttribute("gift-product-ids") || "").split(",");
              var threshold = parseAmount(progress && progress.getAttribute("gift-threshold")) * 100;
              if (cartTotalExcludingGift(cart) < threshold || !hasDiscountedCordon({ items: [target] }, productIds)) {
                throw new Error("This cord is no longer the free gift");
              }
            }

            if (!target || Number(target.id) !== variantId) {
              var replacement = { id: variantId, quantity: 1, properties: replacingRegularLine ? target.properties || {} : { _gift_item: "true" } };
              await giftCartRequest("cart/add.js", { items: [replacement] });
              addedTarget = replacement;
              cart = await giftCartRequest("cart.js");
              var updates = {};
              if (replacingRegularLine) {
                var previousLine = matchingCartLine(cart, target);
                if (!previousLine) throw new Error("Original cord no longer exists");
                updates[previousLine.key] = previousLine.quantity - 1;
              } else {
                cart.items.filter(isGiftLine).forEach(function (item) {
                  updates[item.key] = Number(item.id) === variantId ? 1 : 0;
                });
              }
              await giftCartRequest("cart/update.js", { updates: updates });
              addedTarget = null;
            }
          } catch (error) {
            if (addedTarget) {
              try {
                var rollbackCart = await giftCartRequest("cart.js");
                var addedLine = matchingCartLine(rollbackCart, addedTarget);
                if (addedLine) await giftCartRequest("cart/change.js", { id: addedLine.key, quantity: addedLine.quantity - 1 });
              } catch (rollbackError) {
                console.error("Failed to undo gift replacement:", rollbackError);
              }
            }
            console.error("Failed to select gift:", error);
          } finally {
            this._changeLineKey = null;
            if (modal && typeof modal.hide === "function") await modal.hide();
            await window.openNativeCartDrawer();
            this.classList.remove("is-loading");
          }
        }
      }
    );

  /* ============================================================================================================
     CROSS-SELL QUICK ADD + IPHONE MODEL AUTO-MATCH
     ============================================================================================================ */

  window.customElements.get("cart-cross-sell") ||
    window.customElements.define(
      "cart-cross-sell",
      class extends HTMLElement {
        connectedCallback() {
          this.matchModelVariants();
          if (!this._variantObserver) {
            this._variantObserver = new MutationObserver(function (records) {
              if (records.some(function (record) {
                return Array.from(record.addedNodes).some(function (node) {
                  return node.nodeType === 1 && (node.matches("[data-variant-options]") || node.querySelector("[data-variant-options]"));
                });
              })) {
                this.matchModelVariants();
              }
            }.bind(this));
          }
          this._variantObserver.observe(this, { childList: true, subtree: true });

          if (this._listenersAttached) return;
          this._listenersAttached = true;

          this.addEventListener("click", this.onClick.bind(this));
          this.addEventListener("change", this.onVariantSelectChange.bind(this));
        }

        disconnectedCallback() {
          if (this._variantObserver) this._variantObserver.disconnect();
        }

        // Cart cases take priority over the remembered model. Match complete model tokens,
        // including grouped options, so "17 Pro" never matches "17 Pro Max".
        matchModelVariants() {
          function models(value) {
            var normalized = String(value || "").toLowerCase();
            var result = [];
            normalized.split("/").forEach(function (part) {
              var match = part.trim().match(/^(?:iphone\s*)?(\d{1,2}(?:\s*(?:pro\s*max|pro|plus|mini|air|e|s))?|se(?:\s*\d+)?)$/);
              if (match) result.push(match[1].replace(/\s+/g, ""));
            });
            return result;
          }
          var caseTitles = [];
          try {
            caseTitles = JSON.parse(this.getAttribute("data-cart-case-models") || "[]");
          } catch (e) {}
          var targetModels = [];
          caseTitles.forEach(function (title) {
            // Other product options (colour, finish) may surround the phone model.
            models(title).forEach(function (model) {
              if (!targetModels.includes(model)) targetModels.push(model);
            });
          });
          if (!caseTitles.length) {
            try {
              targetModels = models(localStorage.getItem("iphoneType"));
            } catch (e) {}
          }
          if (!targetModels.length) return;

          this.querySelectorAll("[data-variant-options]").forEach(function (card) {
            var options;
            try {
              options = JSON.parse(card.getAttribute("data-variant-options") || "[]");
            } catch (e) {
              return;
            }

            var match;
            targetModels.some(function (target) {
              match = options.find(function (option) {
                return option.available && models(option.title).includes(target);
              });
              return Boolean(match);
            });

            if (!match) return;

            var select = card.querySelector("[data-variant-select]");
            if (select) select.value = match.id;

            var addButton = card.querySelector("[data-quick-add]");
            if (addButton) addButton.setAttribute("data-variant-id", match.id);

            applyCardVariant(card, match);
          });
        }

        // Manually picking a variant from the dropdown updates the quick-add button, price, compare-at price and
        // image to match — e.g. picking a different charm letter should swap in that letter's own photo, and
        // switching to a discounted model should show its strikethrough price like the product page does.
        onVariantSelectChange(event) {
          var select = event.target.closest("[data-variant-select]");
          if (!select) return;

          var card = select.closest(".cart-cross-sell__card");
          if (!card) return;

          var addButton = card.querySelector("[data-quick-add]");
          if (addButton) addButton.setAttribute("data-variant-id", select.value);

          var options = card.getAttribute("data-variant-options");
          if (!options) return;
          try {
            var match = JSON.parse(options).find(function (option) {
              return String(option.id) === select.value;
            });
            if (match) applyCardVariant(card, match);
          } catch (e) {
            /* noop */
          }
        }

        onClick(event) {
          var button = event.target.closest("[data-quick-add]");
          if (!button || button.hasAttribute("disabled")) return;

          var variantId = Number(button.getAttribute("data-variant-id"));
          if (!variantId) return;

          var originalText = button.textContent;
          button.setAttribute("disabled", "disabled");
          button.textContent = button.getAttribute("data-adding-label") || "...";

          fetch(Shopify.routes.root + "cart/add.js", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
          })
            .then(function () {
              button.textContent = button.getAttribute("data-added-label") || originalText;
              return window.openNativeCartDrawer();
            })
            .catch(function (error) {
              console.error("Cross-sell quick add failed:", error);
              button.removeAttribute("disabled");
              button.textContent = originalText;
            });
        }
      }
    );
})();
